/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/react";
import { useI18n } from "hooks/i18n/i18n";
import { computePeriodStatus } from "pages/DigitalContentDownload/utils/computePeriodStatus";
import { getDownloadLabelKey } from "pages/DigitalContentDownload/utils/getDownloadLabelKey";

import {
  rootStyle,
  titleStyle,
  downloadButtonStyle,
  expiredTextStyle,
  notExpiredTextStyle,
  disabledDownloadButtonStyle,
} from "./styles";

import type { FC } from "react";

type Props = {
  title: string;
  startDate?: string;
  expirationDate?: string;
  downloadUrl?: string;
};

const getPeriodText = (input: {
  startDate?: string;
  expirationDate?: string;
  downloadPeriodLabel: string;
}) => {
  const { startDate, expirationDate, downloadPeriodLabel } = input;
  if (!startDate && !expirationDate) return null;
  const parts = [startDate && `${startDate} ~ `, expirationDate].filter(
    Boolean
  );
  return `${downloadPeriodLabel} ${parts.join("")}`;
};

export const DownloadItem: FC<Props> = (props) => {
  const { title, startDate, expirationDate, downloadUrl } = props;
  const { t } = useI18n();

  const now = new Date();
  const { isBeforeRange, isAfterRange, isPeriodExpired } = computePeriodStatus({
    now,
    startDate,
    expirationDate,
  });

  const isDownloadable = Boolean(downloadUrl && !isPeriodExpired);
  const labelKey = getDownloadLabelKey({
    downloadUrl,
    isBeforeRange,
    isAfterRange,
  });

  const periodText = getPeriodText({
    startDate,
    expirationDate,
    downloadPeriodLabel: t("settlementHistory.downloadPeriod"),
  });

  return (
    <div css={rootStyle}>
      <div>
        <p css={titleStyle}>{title}</p>
      </div>
      {isDownloadable ? (
        <a
          href={downloadUrl}
          css={downloadButtonStyle}
          download
          rel="noopener noreferrer"
        >
          {t(labelKey)}
        </a>
      ) : (
        <span css={disabledDownloadButtonStyle} aria-disabled="true">
          {t(labelKey)}
        </span>
      )}
      {periodText && (
        <p css={isPeriodExpired ? expiredTextStyle : notExpiredTextStyle}>
          {periodText}
        </p>
      )}
    </div>
  );
};
